// apps/frontend/src/lib/pipeline-preview/transformers/filter.ts
import type { PipelineNode, FilterOperator } from '@/pages/pipelines/types/pipeline';
import type { ExecutionContext, TransformerResult } from '../types';
import { inferSchema } from '../utils/schema';

/**
 * Apply FILTER node conditions to data
 */
export async function filterData(
  node: PipelineNode,
  inputData: unknown[],
  _context: ExecutionContext
): Promise<TransformerResult> {
  const startTime = performance.now();

  try {
    const config = node.data.config as any;
    if (!config?.conditions || !Array.isArray(config.conditions)) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'No filter conditions configured',
      };
    }

    const { conditions, operator } = config;
    const filteredData = inputData.filter(row => {
      if (operator === 'AND') {
        return conditions.every((condition: any) => evaluateCondition(row, condition));
      } else {
        // OR
        return conditions.some((condition: any) => evaluateCondition(row, condition));
      }
    });

    // Infer schema from filtered data
    const schema = inferSchema(filteredData);

    const endTime = performance.now();
    return {
      success: true,
      data: filteredData,
      schema,
      stats: {
        rows: filteredData.length,
        executionTime: Math.round(endTime - startTime),
      },
    };
  } catch (error: any) {
    const endTime = performance.now();
    return {
      success: false,
      data: [],
      schema: { columns: [] },
      stats: { rows: 0, executionTime: Math.round(endTime - startTime) },
      error: error.message || 'Filter failed',
    };
  }
}

/**
 * Evaluate a single condition against a row
 */
function evaluateCondition(row: unknown, condition: any): boolean {
  const { field, operator, value } = condition;
  const rowData = row as Record<string, unknown>;
  const fieldValue = rowData[field];

  switch (operator as FilterOperator) {
    case 'eq':
      return fieldValue === value;
    case 'neq':
      return fieldValue !== value;
    case 'gt':
      return (fieldValue as number) > (value as number);
    case 'gte':
      return (fieldValue as number) >= (value as number);
    case 'lt':
      return (fieldValue as number) < (value as number);
    case 'lte':
      return (fieldValue as number) <= (value as number);
    case 'in':
      return Array.isArray(value) && value.includes(fieldValue);
    case 'nin':
      return Array.isArray(value) && !value.includes(fieldValue);
    case 'like':
      return evaluateLike(fieldValue as string, value as string);
    case 'contains':
      return String(fieldValue).includes(String(value));
    default:
      return false;
  }
}

/**
 * Evaluate SQL LIKE pattern
 */
function evaluateLike(fieldValue: string, pattern: string): boolean {
  if (!fieldValue || !pattern) return false;

  // Convert SQL LIKE pattern to regex
  // % = any characters, _ = single character
  const regexPattern = pattern
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape regex special chars
    .replace(/%/g, '.*') // % -> .*
    .replace(/_/g, '.'); // _ -> .

  // Case-insensitive matching (SQL LIKE default in MySQL, configurable in PostgreSQL)
  const regex = new RegExp(`^${regexPattern}$`, 'i');
  return regex.test(String(fieldValue));
}
